/**
 * Route Permissions Map — DENY-BY-DEFAULT
 *
 * Builds a pathname -> requiredPermission map from MODULES_CONFIG + explicit routes.
 * Used by RouteGuard to enforce permission-based access control.
 *
 * IMPORTANT: Routes without any permission mapping are DENIED by default.
 * This prevents security holes where new routes in index.tsx are accessible
 * without having a corresponding permission entry.
 *
 * Resolution order:
 * 1. Public route (/login, /portal, etc.)         -> null (no guard)
 * 2. Unguarded route (/, /test, /dev-tools, etc.)  -> null (auth only)
 * 3. Exact match in EXPLICIT_ROUTE_PERMISSIONS      -> Explicit permission
 * 4. Exact match in MODULES_CONFIG                  -> Sidebar permission
 * 5. Parameterized route match                      -> Pattern permission
 * 6. Wildcard matchPaths from MODULES_CONFIG
 * 7. DENY by default                               -> DENY_ACCESS sentinel
 *
 * Two-level check (same as sidebar):
 * 1. Module permission: e.g. 'dashboard.view'
 * 2. Submodule permission: e.g. { module: 'dashboard', submodule: 'agent' }
 */

import { MODULES_CONFIG } from '@/config/modules-config';

// ============================================================================
// PUBLIC & UNGUARDED ROUTES
// ============================================================================

/** Routes that don't require authentication at all */
export const PUBLIC_ROUTES = [
  '/login',
  '/login-v1',
  '/login-v2',
  '/login-v3',
  '/forgot-password',
  '/reset-password',
  '/public/',
  '/quotes/public/',
  '/orbit-view/',
  '/portal/',
  '/portal-clientes/',
  '/scoring/',
  '/tools',
  '/tools/',
];

/** Routes that require authentication but no specific permission */
const UNGUARDED_ROUTES = [
  '/',
  '/test',
  '/dev-tools',
  '/virtual-scroll-demo',
  '/badge-migration-test',
  '/quote-version-demo',
  '/docs/design-system-2.0',
  '/examples/public-site',
];

// ============================================================================
// TYPES
// ============================================================================

export interface RoutePermission {
  /** Module-level permission (e.g. 'dashboard.view') */
  permission: string;
  /** Submodule check: moduleId + itemId (e.g. { module: 'dashboard', submodule: 'agent' }) */
  submodule?: { module: string; submodule: string };
}

/** Sentinel value returned when a route has no permission mapping (deny by default) */
export const DENY_ACCESS: RoutePermission = Object.freeze({
  permission: '__DENY_BY_DEFAULT__',
});

// ============================================================================
// EXPLICIT ROUTE PERMISSIONS (non-sidebar routes)
// ============================================================================

interface PermissionEntry {
  permission: string;
  submodule?: { module: string; submodule: string };
}

/**
 * Explicit permissions for routes that exist in index.tsx but NOT in MODULES_CONFIG.
 * These are sub-pages, detail views, or aliases that need explicit permission mapping.
 *
 * When adding a new route to index.tsx, also add its permission here
 * (unless it's already in MODULES_CONFIG or matches a parameterized pattern).
 */
const EXPLICIT_ROUTE_PERMISSIONS: Record<string, PermissionEntry> = {
  // --- Leads sub-pages ---
  '/leads/scoring': { permission: 'leads.view', submodule: { module: 'leads', submodule: 'scoring' } },
  '/sales-process': { permission: 'leads.view', submodule: { module: 'leads', submodule: 'funnel' } },

  // --- Tasks alias ---
  '/advisor/tasks': { permission: 'activities.view', submodule: { module: 'activities', submodule: 'tasks' } },

  // --- Analytics sub-pages ---
  '/analytics': { permission: 'analytics.view', submodule: { module: 'analytics', submodule: 'center' } },
  '/analytics/behavior': { permission: 'analytics.view', submodule: { module: 'analytics', submodule: 'center' } },
  '/analytics/product': { permission: 'analytics.view', submodule: { module: 'analytics', submodule: 'center' } },

  // --- Chat/AI config alias ---
  '/chat/config': { permission: 'analytics.view', submodule: { module: 'analytics', submodule: 'agent-config' } },

  // --- Omnichannel sub-pages ---
  '/omnichannel/call': { permission: 'communications.view', submodule: { module: 'communications', submodule: 'omnichannel' } },
  '/omnichannel/video': { permission: 'communications.view', submodule: { module: 'communications', submodule: 'omnichannel' } },
  '/omnichannel/meeting': { permission: 'communications.view', submodule: { module: 'communications', submodule: 'omnichannel' } },

  // --- AI Center sub-pages ---
  '/ai-center/conversations': { permission: 'analytics.view', submodule: { module: 'analytics', submodule: 'center' } },

  // --- Legacy/alias routes ---
  '/global-area-types': { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'global-config' } },

  // --- Financial sub-pages not in sidebar ---
  '/financial/income-categories': { permission: 'financial.view', submodule: { module: 'financial', submodule: 'incomes' } },
  '/financial/cost-categories': { permission: 'financial.view', submodule: { module: 'financial', submodule: 'costs' } },

  // --- AWS Management sub-pages ---
  '/aws-management/costs': { permission: 'admin.view', submodule: { module: 'admin', submodule: 'aws' } },
  '/aws-management/services': { permission: 'admin.view', submodule: { module: 'admin', submodule: 'aws' } },
  '/aws-management/forecasts': { permission: 'admin.view', submodule: { module: 'admin', submodule: 'aws' } },
  '/aws-management/projector': { permission: 'admin.view', submodule: { module: 'admin', submodule: 'aws' } },

  // --- Payroll sub-pages ---
  '/payroll/employees': { permission: 'admin.view', submodule: { module: 'admin', submodule: 'payroll' } },
  '/payroll/employees/new': { permission: 'admin.view', submodule: { module: 'admin', submodule: 'payroll' } },
  '/payroll/payrolls': { permission: 'admin.view', submodule: { module: 'admin', submodule: 'payroll' } },
  '/payroll/salary-components': { permission: 'admin.view', submodule: { module: 'admin', submodule: 'payroll' } },

  // --- Billing sub-pages ---
  '/billing/invoices': { permission: 'clients.view', submodule: { module: 'clients', submodule: 'billing' } },
  '/billing/plans': { permission: 'clients.view', submodule: { module: 'clients', submodule: 'billing' } },

  // --- Users sub-pages ---
  '/users/new': { permission: 'team.view', submodule: { module: 'team', submodule: 'users' } },
  '/roles/create': { permission: 'team.view', submodule: { module: 'team', submodule: 'roles' } },
  '/permissions/matrix': { permission: 'team.view', submodule: { module: 'team', submodule: 'permissions' } },

  // --- Companies sub-pages ---
  '/companies/create': { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'companies' } },

  // --- SINCO ERP integration (project mapping pages) ---
  '/integrations/sinco/projects': { permission: 'analytics.view', submodule: { module: 'analytics', submodule: 'integrations' } },
  '/system/integrations/sinco/projects': { permission: 'system.view', submodule: { module: 'system', submodule: 'integrations' } },
};

// ============================================================================
// PARAMETERIZED ROUTE PERMISSIONS (routes with :params)
// ============================================================================

interface ParameterizedPattern {
  /** URL segments, e.g. ['projects', ':id', ':tab']. Segments starting with ':' match any value. */
  segments: string[];
  entry: PermissionEntry;
}

/**
 * Permission patterns for parameterized routes (routes with :id, :tab, etc.).
 * Static segments must match exactly; segments starting with ':' match any value.
 */
const PARAMETERIZED_ROUTE_PERMISSIONS: ParameterizedPattern[] = [
  // --- Project hierarchy ---
  { segments: ['projects', ':id'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'projects' } } },
  { segments: ['projects', ':id', ':tab'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'projects' } } },
  { segments: ['projects', ':projectId', 'stages', ':stageId'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'projects' } } },
  { segments: ['projects', ':projectId', 'stages', ':stageId', ':tab'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'projects' } } },
  { segments: ['projects', ':projectId', 'stages', ':stageId', 'blocks', ':blockId'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'projects' } } },
  { segments: ['projects', ':projectId', 'stages', ':stageId', 'typologies', ':typologyId'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'projects' } } },
  { segments: ['projects', ':projectId', 'amenities', ':amenityId'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'projects' } } },
  { segments: ['projects', ':projectId', 'stages', ':stageId', 'amenities', ':amenityId'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'projects' } } },
  { segments: ['projects', ':projectId', 'stages', ':stageId', 'blocks', ':blockId', 'amenities', ':amenityId'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'projects' } } },
  { segments: ['projects', ':projectId', 'stages', ':stageId', 'price-lists', ':priceListId'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'projects' } } },
  { segments: ['projects', ':projectId', 'stages', ':stageId', 'blocks', ':blockId', 'units', ':unitId'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'projects' } } },
  { segments: ['projects', ':projectId', 'units'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'projects' } } },
  { segments: ['projects', ':projectSlug', 'orbit-view', ':slug'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'projects' } } },

  // --- Users ---
  { segments: ['users', ':id'], entry: { permission: 'team.view', submodule: { module: 'team', submodule: 'users' } } },
  { segments: ['users', ':id', 'edit'], entry: { permission: 'team.view', submodule: { module: 'team', submodule: 'users' } } },

  // --- Companies ---
  { segments: ['companies', ':id'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'companies' } } },

  // --- Markets ---
  { segments: ['markets', ':id'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'markets' } } },
  { segments: ['markets', ':id', ':tab'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'markets' } } },

  // --- Global config ---
  { segments: ['global-config', ':tab'], entry: { permission: 'gestion.view', submodule: { module: 'gestion', submodule: 'global-config' } } },

  // --- Leads ---
  { segments: ['leads', ':id', 'insights'], entry: { permission: 'leads.view', submodule: { module: 'leads', submodule: 'list' } } },
  { segments: ['leads', ':id'], entry: { permission: 'leads.view', submodule: { module: 'leads', submodule: 'list' } } },
  { segments: ['leads', 'scoring', ':tab'], entry: { permission: 'leads.view', submodule: { module: 'leads', submodule: 'scoring' } } },
  { segments: ['lead-scoring', ':tab'], entry: { permission: 'leads.view', submodule: { module: 'leads', submodule: 'scoring' } } },

  // --- Clients ---
  { segments: ['clients', ':clientId'], entry: { permission: 'clients.view', submodule: { module: 'clients', submodule: 'list' } } },

  // --- AI Agents ---
  { segments: ['ai', 'agents', ':code'], entry: { permission: 'analytics.view', submodule: { module: 'ai-agents', submodule: 'agents-list' } } },
  { segments: ['ai', 'knowledge', ':id'], entry: { permission: 'analytics.view', submodule: { module: 'ai-agents', submodule: 'knowledge-packs' } } },

  // --- Owly BI (Custom Dashboards) ---
  { segments: ['dashboard', 'owly-bi', ':dashboardId'], entry: { permission: 'dashboard.view', submodule: { module: 'dashboard', submodule: 'owly-bi' } } },

  // --- Content Hub ---
  { segments: ['marketing', 'content', 'blog', ':id'], entry: { permission: 'marketing.view', submodule: { module: 'marketing', submodule: 'content-blog' } } },
  { segments: ['marketing', 'content', 'blog', ':id', 'pieces'], entry: { permission: 'marketing.view', submodule: { module: 'marketing', submodule: 'content-blog' } } },
];

// ============================================================================
// MODULES_CONFIG PERMISSION MAP (sidebar-derived)
// ============================================================================

interface PermissionMapping {
  exactMap: Map<string, PermissionEntry>;
  wildcardItems: Array<{ pattern: string; entry: PermissionEntry }>;
}

/** Build the permission map from MODULES_CONFIG (computed once) */
function buildPermissionMap(): PermissionMapping {
  const exactMap = new Map<string, PermissionEntry>();
  const wildcardItems: Array<{ pattern: string; entry: PermissionEntry }> = [];

  for (const module of MODULES_CONFIG) {
    for (const item of module.items) {
      if (item.requiredPermission && item.href) {
        const entry: PermissionEntry = {
          permission: item.requiredPermission,
          submodule: { module: module.id, submodule: item.id },
        };
        exactMap.set(item.href, entry);

        // Collect wildcard patterns
        if (item.matchPaths) {
          for (const pattern of item.matchPaths) {
            wildcardItems.push({ pattern, entry });
          }
        }
      }
    }
  }

  return { exactMap, wildcardItems };
}

// Singleton - computed once at module load
const PERMISSION_MAP = buildPermissionMap();

// ============================================================================
// PARAMETERIZED ROUTE MATCHER
// ============================================================================

/**
 * Match a pathname against parameterized route patterns.
 * Pattern segments starting with ':' match any value.
 * Static segments must match exactly.
 */
function matchParameterizedRoute(pathname: string): PermissionEntry | null {
  const pathSegments = pathname.split('/').filter(Boolean);

  for (const { segments: patternSegments, entry } of PARAMETERIZED_ROUTE_PERMISSIONS) {
    if (pathSegments.length !== patternSegments.length) continue;

    let matches = true;
    for (let i = 0; i < patternSegments.length; i++) {
      // Parameter segments (starting with ':') match anything
      if (patternSegments[i].startsWith(':')) continue;
      // Static segments must match exactly
      if (patternSegments[i] !== pathSegments[i]) {
        matches = false;
        break;
      }
    }

    if (matches) return entry;
  }

  return null;
}

// ============================================================================
// MAIN FUNCTION
// ============================================================================

/**
 * Find the required permission for a given pathname.
 *
 * @returns RoutePermission with module/submodule info, DENY_ACCESS sentinel, or null (public/unguarded)
 */
export function findPermissionForPath(rawPathname: string): RoutePermission | null {
  // Normalize: strip trailing slash (except root /)
  const pathname = rawPathname.length > 1 && rawPathname.endsWith('/')
    ? rawPathname.slice(0, -1)
    : rawPathname;

  // 1. Check public routes (prefix match)
  for (const publicRoute of PUBLIC_ROUTES) {
    if (pathname === publicRoute || pathname.startsWith(publicRoute)) {
      return null;
    }
  }

  // 2. Check unguarded routes (auth required, no permission check)
  for (const route of UNGUARDED_ROUTES) {
    if (pathname === route || pathname.startsWith(route + '/')) {
      return null;
    }
  }

  // 3. Explicit route permissions (non-sidebar routes)
  const explicitMatch = EXPLICIT_ROUTE_PERMISSIONS[pathname];
  if (explicitMatch) {
    return explicitMatch;
  }

  // 4. MODULES_CONFIG exact match (sidebar routes)
  const exactMatch = PERMISSION_MAP.exactMap.get(pathname);
  if (exactMatch) {
    return exactMatch;
  }

  // 5. Parameterized route match (routes with :params)
  const paramMatch = matchParameterizedRoute(pathname);
  if (paramMatch) {
    return paramMatch;
  }

  // 6. Wildcard matching (matchPaths from MODULES_CONFIG, e.g. /automations/*)
  for (const { pattern, entry } of PERMISSION_MAP.wildcardItems) {
    if (matchWildcard(pattern, pathname)) {
      return entry;
    }
  }

  // 7. DENY by default — route has no permission mapping
  if (import.meta.env.DEV) {
    console.warn(
      `[RouteGuard] Ruta sin permisos configurados: "${pathname}". ` +
      `Acceso denegado por defecto. Agrega esta ruta a EXPLICIT_ROUTE_PERMISSIONS, ` +
      `PARAMETERIZED_ROUTE_PERMISSIONS, o MODULES_CONFIG.`
    );
  }
  return DENY_ACCESS;
}

/**
 * Simple wildcard matcher.
 * Supports trailing `*` (e.g. `/automations/*` matches `/automations/123/versions/456`)
 */
function matchWildcard(pattern: string, pathname: string): boolean {
  if (pattern.endsWith('/*')) {
    const prefix = pattern.slice(0, -1);
    return pathname.startsWith(prefix);
  }
  if (pattern.endsWith('*')) {
    const prefix = pattern.slice(0, -1);
    return pathname.startsWith(prefix);
  }
  return pathname === pattern;
}
